import {
  canSeeTestProfessionals,
  isTestProfessional,
  noteTestProfessionalsFromUserScopedResponse,
} from '../utils/testProfessional';

const API_BASE_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';

// Sesión Odoo activa (id numérico + token) o null. El id puede venir del login
// directo (daisies_user_data.daisUserId/.id) o del bootstrap desde landing
// (daisies_user_id). Un id no numérico (firebase uid) no sirve para res_id.
const getOdooSession = () => {
  try {
    const token =
      localStorage.getItem('daisies_auth_token') ||
      document.cookie.match(/(?:^|; )daisies_auth_token=([^;]*)/)?.[1] ||
      '';
    if (!token) return null;
    let odooUserId = parseInt(localStorage.getItem('daisies_user_id'), 10);
    if (!odooUserId) {
      const raw = localStorage.getItem('daisies_user_data');
      const u = raw ? JSON.parse(raw) : null;
      odooUserId = parseInt(u?.daisUserId ?? u?.id, 10);
    }
    if (!odooUserId || Number.isNaN(odooUserId)) return null;
    return { odooUserId, authToken: decodeURIComponent(token) };
  } catch (e) {
    return null;
  }
};

// Quita doctoras de prueba (is_test_professional) de una respuesta de
// disponibilidad por fecha (create_include_consultation_by_user_v2_public).
// Si un slot queda sin doctoras reales, se descarta el slot completo. Respeta
// el gate de localhost/usuario interno. Mutar aquí protege a TODOS los callers
// del endpoint público (ScheduleSection y getAvailabilityByDateAllDurations).
const stripTestProfessionalsFromAvailability = (data) => {
  if (!data || canSeeTestProfessionals()) return data;
  const days = Array.isArray(data.result) ? data.result : (Array.isArray(data) ? data : null);
  if (!days) return data;
  days.forEach(day => {
    if (!day || !Array.isArray(day.schedule_available)) return;
    day.schedule_available = day.schedule_available.filter(slot => {
      const profs = slot && Array.isArray(slot.professional_ids) ? slot.professional_ids : null;
      if (!profs || profs.length === 0) return true; // sin info de doctoras → dejar slot
      const real = profs.filter(p => !isTestProfessional(p));
      slot.professional_ids = real;
      return real.length > 0; // descartar slot si TODAS eran de prueba
    });
  });
  return data;
};

// Filtros compartidos para listas de doctoras. Descarta:
// - registros inválidos (sin id)
// - doctoras de prueba (is_test_professional) salvo localhost/usuario interno
// - inactivas (active === false) o en estado de lista negra
// - opcionalmente, sin especialidad asignada (registro huérfano)
// Ya NO se filtra por next_closest_* / professional_availability_count: las
// altas especialidades de baja demanda (Psiquiatría, Terapia en pareja,
// Asesoría en lactancia) tienen count=0 y next_closest_* vacío aunque abren
// agenda esporádicamente; mejor mostrarlas y dejar que el modal indique
// "sin disponibilidad" que bloquear la especialidad completa.
const filterDoctorList = (allDoctors, specialtyId, { filterBySpecialty = false, requireSpecialty = false } = {}) => {
  const INACTIVE_STATES = new Set(['inactive', 'unavailable', 'disabled', 'archived', 'deleted']);
  const showTest = canSeeTestProfessionals();
  const rejected = [];
  let list = (allDoctors || []).filter(doctor => {
    if (!doctor || !doctor.id) return false;
    if (!showTest && isTestProfessional(doctor)) { rejected.push({ d: doctor, why: 'is_test_professional' }); return false; }
    if (doctor.active === false) { rejected.push({ d: doctor, why: 'inactive' }); return false; }
    if (doctor.state && INACTIVE_STATES.has(String(doctor.state).toLowerCase())) { rejected.push({ d: doctor, why: `state=${doctor.state}` }); return false; }
    if (requireSpecialty) {
      const hasSpecialty = doctor.specialty?.id || (Array.isArray(doctor.specialty_ids) && doctor.specialty_ids.length > 0);
      if (!hasSpecialty) { rejected.push({ d: doctor, why: 'no specialty' }); return false; }
    }
    return true;
  });

  if (rejected.length > 0) {
    console.log(`[getDoctorsBySpecialty] Filtered ${rejected.length} of ${(allDoctors || []).length} doctors:`,
      rejected.map(r => `${r.d.name} ${r.d.last_name} → ${r.why}`));
  }

  if (filterBySpecialty && specialtyId) {
    const targetId = parseInt(specialtyId);
    list = list.filter(doctor =>
      doctor.specialty?.id === targetId ||
      doctor.specialty_ids?.some(spec => spec.id === targetId)
    );
  }

  return list;
};

export const doctorService = {
  // Lista de doctoras por especialidad.
  //
  // Con sesión Odoo iniciada usamos `get_professional_sequence_for_user`
  // (res_id = user, igual que Flutter): el backend gatea ahí las doctoras de
  // prueba por `dais.user.user_type`, así que un usuario dais_test SÍ las
  // recibe. El endpoint de externos las filtra incondicionalmente server-side
  // y solo queda para sesiones anónimas (o como fallback si el user-scoped
  // falla). filterDoctorList sigue aplicando el resto de filtros locales.
  async getDoctorsBySpecialty(specialtyId, userId = 71403) {
    const session = getOdooSession();
    if (session) {
      try {
        const userPayload = {
          params: {
            access_token: process.env.REACT_APP_ACCESS_TOKEN,
            account_id: 1,
            auth_token: session.authToken,
            res_id: session.odooUserId,
            res_model: 'dais.user',
            res_method: 'get_professional_sequence_for_user',
            res_params: {},
          },
        };
        const response = await fetch(API_BASE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(userPayload),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.result?.success) {
            // Respuesta gateada por el backend: si trae doctoras de prueba,
            // esta cuenta está autorizada → habilita el gate local.
            noteTestProfessionalsFromUserScopedResponse(data.result.data);
            return filterDoctorList(data.result.data || [], specialtyId, {
              filterBySpecialty: true,
              requireSpecialty: true,
            });
          }
        }
        // Sin success → caemos al endpoint de externos.
      } catch (error) {
        console.warn('[getDoctorsBySpecialty] user-scoped falló, fallback a externals', error);
      }
    }

    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: 1,
        res_id: 1,
        res_model: 'dais.user',
        res_method: 'get_professional_sequence_for_user_for_externals',
        res_params: {},
      },
    };
    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data.result?.success) {
        return filterDoctorList(data.result.data || [], specialtyId, {
          filterBySpecialty: true,
          requireSpecialty: true,
        });
      }
      throw new Error(data.result?.error || 'Failed to fetch doctors');
    } catch (error) {
      throw error;
    }
  },

  async getDoctorSchedule(doctorId, authToken, doctorTz = 'America/Mexico_City', durationId = 109) {
    // Calcular fechas: hoy + 7 días
    const today = new Date();
    const sevenDaysLater = new Date(today);
    sevenDaysLater.setDate(today.getDate() + 7);

    const formatDate = (date) => {
      return date.toISOString().split('T')[0];
    };

    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        auth_token: authToken || '<auth_token_needed>',
        account_id: 1,
        res_id: 0,
        res_model: 'dais.consultation',
        res_method: 'get_professional_schedule_today_for_externals',
        res_params: {
          doctor_id: parseInt(doctorId),
          date_start: formatDate(today),
          date_end: formatDate(sevenDaysLater),
          duration_id: durationId, // Usar el durationId que viene del parámetro
          tz: doctorTz
        }
      }
    };
    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.result?.success) {
        // Encontrar el horario sugerido (primer horario disponible)
        let suggestedSlot = null;
        const allSlots = [];
        
        // Procesar los datos para encontrar el primer horario disponible
        data.result.data.forEach(day => {
          day.schedule_available.forEach(slot => {
            allSlots.push({
              ...slot,
              week_day: day.week_day,
              date: day.date
            });
            
            // Si encontramos el primer horario disponible, lo guardamos como sugerido
            if (!suggestedSlot && slot.available) {
              suggestedSlot = {
                ...slot,
                week_day: day.week_day,
                date: day.date
              };
            }
          });
        });
        
        // Si no hay horarios disponibles, usar el primer horario del primer día
        if (!suggestedSlot && data.result.data.length > 0 && 
            data.result.data[0].schedule_available.length > 0) {
          suggestedSlot = {
            ...data.result.data[0].schedule_available[0],
            week_day: data.result.data[0].week_day,
            date: data.result.data[0].date
          };
        }
        
        return {
          ...data.result,
          suggestedSlot: suggestedSlot,
          allSlots: allSlots
        };
      } else {
        const errorMsg = data.result?.error || data.error || 'Failed to fetch schedule';
        throw new Error(errorMsg);
      }
    } catch (error) {
      throw error;
    }
  },

  /**
   * Fetch doctor schedule for ALL durations in parallel and merge results.
   * @param {number} doctorId
   * @param {string} authToken
   * @param {string} doctorTz
   * @param {Array<{id: number, isExtra: boolean}>} allDurations — every duration to fetch
   * @returns merged schedule in the same shape as getDoctorSchedule
   */
  async getDoctorScheduleAllDurations(doctorId, authToken, doctorTz = 'America/Mexico_City', allDurations = []) {
    if (!allDurations || allDurations.length === 0) {
      // Fallback: single call with default
      return this.getDoctorSchedule(doctorId, authToken, doctorTz, 109);
    }

    const today = new Date();
    const sevenDaysLater = new Date(today);
    sevenDaysLater.setDate(today.getDate() + 7);
    const formatDate = (date) => date.toISOString().split('T')[0];

    // Build one fetch per duration
    const fetchForDuration = async (durObj) => {
      const payload = {
        params: {
          access_token: process.env.REACT_APP_ACCESS_TOKEN,
          auth_token: authToken || '<auth_token_needed>',
          account_id: 1,
          res_id: 0,
          res_model: 'dais.consultation',
          res_method: 'get_professional_schedule_today_for_externals',
          res_params: {
            doctor_id: parseInt(doctorId),
            date_start: formatDate(today),
            date_end: formatDate(sevenDaysLater),
            duration_id: durObj.id,
            tz: doctorTz
          }
        }
      };

      try {
        const response = await fetch(API_BASE_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) {
          console.warn(`[getDoctorScheduleAllDurations] HTTP error for duration ${durObj.id}: ${response.status}`);
          return null;
        }

        const data = await response.json();
        if (data.result?.success) {
          return { data: data.result, durObj };
        }
        console.warn(`[getDoctorScheduleAllDurations] API error for duration ${durObj.id}:`, data.result?.error);
        return null;
      } catch (err) {
        console.warn(`[getDoctorScheduleAllDurations] Fetch error for duration ${durObj.id}:`, err);
        return null;
      }
    };

    // Fire all in parallel
    const results = await Promise.all(allDurations.map(d => fetchForDuration(d)));

    // Merge all results into a unified schedule keyed by date
    const dayMap = {}; // date -> { week_day, slots: Map<slotKey, slot> }

    results.forEach(result => {
      if (!result) return;
      const { data, durObj } = result;

      (data.data || []).forEach(day => {
        if (!dayMap[day.date]) {
          dayMap[day.date] = { week_day: day.week_day, date: day.date, slots: new Map() };
        }

        (day.schedule_available || []).forEach(slot => {
          // Unique key: include duration_id so each duration keeps its own copy
          const key = `${slot.id}-${slot.hour}-${durObj.id}`;
          if (!dayMap[day.date].slots.has(key)) {
            dayMap[day.date].slots.set(key, {
              ...slot,
              duration_id: durObj.id,
              isExtra: durObj.isExtra
            });
          }
        });
      });
    });

    // Convert to the array format expected by DoctorScheduleModal
    const mergedData = Object.values(dayMap)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(day => ({
        week_day: day.week_day,
        date: day.date,
        schedule_available: Array.from(day.slots.values())
          .sort((a, b) => (a.hour || '').localeCompare(b.hour || ''))
      }));

    // Find first available slot as suggested
    let suggestedSlot = null;
    const allSlots = [];

    mergedData.forEach(day => {
      day.schedule_available.forEach(slot => {
        allSlots.push({ ...slot, week_day: day.week_day, date: day.date });
        if (!suggestedSlot && slot.available) {
          suggestedSlot = { ...slot, week_day: day.week_day, date: day.date };
        }
      });
    });

    if (!suggestedSlot && mergedData.length > 0 && mergedData[0].schedule_available.length > 0) {
      suggestedSlot = {
        ...mergedData[0].schedule_available[0],
        week_day: mergedData[0].week_day,
        date: mergedData[0].date
      };
    }

    const hasAvailability = allSlots.some(s => s.available);

    return {
      success: true,
      data: mergedData,
      has_availability: hasAvailability,
      suggestedSlot,
      allSlots
    };
  },

  /**
   * Public API: Fetch availability by date using create_include_consultation_by_user_v2_public.
   * Does NOT require auth_token or user_id.
   * @param {number} specialtyId
   * @param {number} durationId
   * @param {string} date - YYYY-MM-DD
   * @param {boolean} isExtra
   * @param {string} tz - timezone
   * @returns {Promise<Object>} API response
   */
  async getAvailabilityByDatePublic(specialtyId, durationId, date, isExtra = false, tz = 'America/Mexico_City') {
    const payload = {
      params: {
        access_token: process.env.REACT_APP_ACCESS_TOKEN,
        account_id: 1,
        res_id: 1,
        res_model: 'dais.consultation',
        res_method: 'create_include_consultation_by_user_v2_public',
        res_params: {
          specialty_id: specialtyId,
          duration_id: durationId,
          date: date,
          is_extra: isExtra,
          tz: tz
        }
      }
    };

    try {
      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        console.warn(`[getAvailabilityByDatePublic] HTTP error: ${response.status}`);
        return null;
      }

      const data = await response.json();
      // Filtrar doctoras de prueba antes de devolver (ScheduleSection del home
      // renderiza professional_ids[0] directo y era el path por el que Bárbara
      // Fernández seguía apareciendo siempre).
      return stripTestProfessionalsFromAvailability(data);
    } catch (err) {
      console.warn('[getAvailabilityByDatePublic] Fetch error:', err);
      return null;
    }
  },

  /**
   * Fetch availability for ALL durations in parallel for the date-first flow.
   * Each duration call is tagged with isExtra + durationMinutes for sectioning.
   * @param {number} specialtyId
   * @param {string} date - YYYY-MM-DD
   * @param {Array<{id: number, isExtra: boolean, durationMinutes: number|null}>} allDurations
   * @param {string} tz
   * @returns {Object} organized availability data keyed by date, slots tagged with section info
   */
  async getAvailabilityByDateAllDurations(specialtyId, date, allDurations, tz = 'America/Mexico_City') {
    if (!allDurations || allDurations.length === 0) {
      console.warn('[getAvailabilityByDateAllDurations] No durations provided');
      return {};
    }

    // Fire all in parallel
    const results = await Promise.all(
      allDurations.map(durObj =>
        this.getAvailabilityByDatePublic(specialtyId, durObj.id, date, durObj.isExtra, tz)
          .then(data => ({ data, durObj }))
          .catch(err => {
            console.warn(`[getAvailabilityByDateAllDurations] Error for duration ${durObj.id}:`, err);
            return null;
          })
      )
    );

    // Organize all slots by date with section metadata
    const organized = {};
    // Doctoras de prueba: ocultas salvo en localhost o usuario interno.
    const showTest = canSeeTestProfessionals();

    results.forEach(result => {
      if (!result || !result.data) return;

      const { data, durObj } = result;
      // The API returns an array of day objects directly (not wrapped in result.data)
      const dayArray = data.result || data;

      if (!Array.isArray(dayArray)) {
        console.warn('[getAvailabilityByDateAllDurations] Unexpected response format:', data);
        return;
      }

      dayArray.forEach(dayData => {
        const dateKey = dayData.date;
        if (!organized[dateKey]) {
          organized[dateKey] = [];
        }

        const scheduleAvailable = dayData.schedule_available || [];
        scheduleAvailable.forEach(slot => {
          // Filter out test professionals; if the slot is left without doctoras, skip it.
          const allProfs = slot.professional_ids || [];
          const filteredProfs = showTest ? allProfs : allProfs.filter(p => !isTestProfessional(p));
          if (!showTest && allProfs.length > 0 && filteredProfs.length === 0) {
            return;
          }

          // Build a unique key per slot+duration to avoid cross-duration dedup
          const slotKey = `${slot.init}-${slot.end}-${durObj.id}-${durObj.isExtra}`;
          const exists = organized[dateKey].some(s =>
            `${s.init}-${s.end}-${s.duration_id}-${s.isExtra}` === slotKey
          );

          if (!exists) {
            // Build section label
            let sectionLabel;
            if (durObj.isExtra) {
              sectionLabel = `Consulta extra${durObj.durationMinutes ? ` — ${durObj.durationMinutes} min` : ''}`;
            } else {
              sectionLabel = `Consulta incluida${durObj.durationMinutes ? ` — ${durObj.durationMinutes} min` : ''}`;
            }

            organized[dateKey].push({
              id: slot.id,
              init: slot.init,
              end: slot.end,
              professional_ids: filteredProfs,
              duration_id: durObj.id,
              isExtra: durObj.isExtra,
              durationMinutes: durObj.durationMinutes || null,
              sectionLabel: sectionLabel,
              date: dateKey,
              week_day: dayData.week_day
            });
          }
        });
      });
    });

    // Sort each date's slots: extras first, then by durationMinutes desc, then by time
    Object.keys(organized).forEach(dateKey => {
      organized[dateKey].sort((a, b) => {
        // 1. Extras first
        if (a.isExtra !== b.isExtra) return a.isExtra ? -1 : 1;
        // 2. Among same type, by durationMinutes descending
        if ((b.durationMinutes || 0) !== (a.durationMinutes || 0)) {
          return (b.durationMinutes || 0) - (a.durationMinutes || 0);
        }
        // 3. By time
        const timeA = a.init.split(':').map(Number);
        const timeB = b.init.split(':').map(Number);
        return (timeA[0] * 60 + (timeA[1] || 0)) - (timeB[0] * 60 + (timeB[1] || 0));
      });
    });

    return organized;
  }
};